import { requireSupabase } from "@/lib/supabaseClient";
import { guard } from "@/lib/errors";
import { cached } from "@/lib/cache";

const TTL = 10 * 60 * 1000; // geography rarely changes

const SELECT = "id, name, code, latitude, longitude, alt_names, sort_order, metadata";
const order = (q) => q.order("sort_order").order("name");

/**
 * Hierarchical Ethiopian geography (Phase 1).
 * Country -> Region -> Zone -> City -> Sub-city -> Woreda -> Neighborhood
 *
 * All lower levels are OPTIONAL: callers may stop at any level. Each helper
 * gracefully returns [] when the backend/tables are not yet available.
 */
export const geographyService = {
  countries: () => cached("geo:countries", () => guard("geo.countries", async () => {
    const s = requireSupabase();
    const { data, error } = await order(s.from("countries").select(SELECT).eq("is_active", true));
    if (error) throw error; return data || [];
  }), TTL),

  regions: (countryId) => cached(`geo:regions:${countryId || "all"}`, () => guard("geo.regions", async () => {
    const s = requireSupabase();
    let q = s.from("regions").select(`${SELECT}, country_id`);
    if (countryId) q = q.eq("country_id", countryId);
    const { data, error } = await order(q);
    if (error) throw error; return data || [];
  }), TTL),

  zones: (regionId) => {
    if (!regionId) return Promise.resolve([]);
    return cached(`geo:zones:${regionId}`, () => guard("geo.zones", async () => {
      const s = requireSupabase();
      const { data, error } = await order(s.from("zones").select(`${SELECT}, region_id`).eq("region_id", regionId).eq("is_active", true));
      if (error) throw error; return data || [];
    }), TTL);
  },

  // Cities filtered by zone when provided, otherwise by region.
  cities: ({ regionId, zoneId } = {}) => {
    const key = zoneId ? `zone:${zoneId}` : `region:${regionId || "all"}`;
    return cached(`geo:cities:${key}`, () => guard("geo.cities", async () => {
      const s = requireSupabase();
      let q = s.from("cities").select(`${SELECT}, region_id, zone_id`);
      if (zoneId) q = q.eq("zone_id", zoneId);
      else if (regionId) q = q.eq("region_id", regionId);
      const { data, error } = await order(q);
      if (error) throw error; return data || [];
    }), TTL);
  },

  subCities: (cityId) => {
    if (!cityId) return Promise.resolve([]);
    return cached(`geo:subcities:${cityId}`, () => guard("geo.subCities", async () => {
      const s = requireSupabase();
      const { data, error } = await order(s.from("sub_cities").select(`${SELECT}, city_id`).eq("city_id", cityId).eq("is_active", true));
      if (error) throw error; return data || [];
    }), TTL);
  },

  // Woredas nested under a sub-city when provided, otherwise directly under a city.
  woredas: ({ cityId, subCityId } = {}) => {
    if (!cityId && !subCityId) return Promise.resolve([]);
    const key = subCityId ? `sub:${subCityId}` : `city:${cityId}`;
    return cached(`geo:woredas:${key}`, () => guard("geo.woredas", async () => {
      const s = requireSupabase();
      let q = s.from("woredas").select(`${SELECT}, city_id, sub_city_id`);
      if (subCityId) q = q.eq("sub_city_id", subCityId);
      else q = q.eq("city_id", cityId).is("sub_city_id", null);
      const { data, error } = await order(q);
      if (error) throw error; return data || [];
    }), TTL);
  },

  // Neighborhoods filtered by the most specific parent available.
  neighborhoods: ({ cityId, subCityId, woredaId } = {}) => {
    if (!cityId && !subCityId && !woredaId) return Promise.resolve([]);
    const key = woredaId ? `w:${woredaId}` : subCityId ? `s:${subCityId}` : `c:${cityId}`;
    return cached(`geo:neighborhoods:${key}`, () => guard("geo.neighborhoods", async () => {
      const s = requireSupabase();
      let q = s.from("neighborhoods").select(`${SELECT}, city_id, sub_city_id, woreda_id`);
      if (woredaId) q = q.eq("woreda_id", woredaId);
      else if (subCityId) q = q.eq("sub_city_id", subCityId);
      else q = q.eq("city_id", cityId);
      const { data, error } = await order(q);
      if (error) throw error; return data || [];
    }), TTL);
  },

  /**
   * Universal location search across cities, sub-cities and neighborhoods.
   * Matches name OR alt_names (landmark aliases). Returns a unified, ranked list
   * for the location autocomplete box. Gracefully returns [] on any failure.
   */
  search: (term, { limit = 8 } = {}) => guard("geo.search", async () => {
    const t = (term || "").trim();
    if (t.length < 2) return [];
    const s = requireSupabase();
    const like = `%${t}%`;
    const pick = (level, rows) => (rows || []).map((r) => ({
      level,
      id: r.id,
      name: r.name,
      latitude: r.latitude,
      longitude: r.longitude,
      city_id: r.city_id || null,
      sub_city_id: r.sub_city_id || null,
      woreda_id: r.woreda_id || null,
      region_id: r.region_id || null,
    }));
    const [cities, subs, hoods] = await Promise.all([
      s.from("cities").select("id,name,latitude,longitude,region_id").ilike("name", like).limit(limit)
        .then(({ data }) => pick("city", data)).catch(() => []),
      s.from("sub_cities").select("id,name,latitude,longitude,city_id").ilike("name", like).limit(limit)
        .then(({ data }) => pick("sub_city", data)).catch(() => []),
      s.from("neighborhoods").select("id,name,latitude,longitude,city_id,sub_city_id,woreda_id").ilike("name", like).limit(limit)
        .then(({ data }) => pick("neighborhood", data)).catch(() => []),
    ]);
    // Rank exact/prefix matches first, then by level specificity.
    const tl = t.toLowerCase();
    const score = (r) => {
      const n = r.name?.toLowerCase() || "";
      let sc = n === tl ? 0 : n.startsWith(tl) ? 1 : 2;
      sc = sc * 10 + ({ neighborhood: 0, sub_city: 1, city: 2 }[r.level] ?? 3);
      return sc;
    };
    return [...hoods, ...subs, ...cities].sort((a, b) => score(a) - score(b)).slice(0, limit);
  }).catch(() => []),
};
